import type Player from 'video.js/dist/types/player';
import throttle from 'lodash.throttle';
import type { BrazeAnalyticsConfiguration } from '~/services/analytics/events-names.ts';
import type { PlayerTrackData } from '../tracking.ts';
import type { FeedEntry } from '~/services/layout/index.ts';

type BrazeAnalyticsPluginOptions = {
  brazeUserId?: string;
  brazeAnalytics: BrazeAnalyticsConfiguration;
  trackData: PlayerTrackData;
  playableItem: FeedEntry;
};

const EVENTS = [
  // HTMLMediaElement events
  'abort',
  'canplay',
  'canplaythrough',
  'durationchange',
  'emptied',
  'ended',
  'error',
  'loadeddata',
  'loadedmetadata',
  'loadstart',
  'pause',
  'play',
  'playing',
  'progress',
  'ratechange',
  'seeked',
  'seeking',
  'stalled',
  'suspend',
  'timeupdate',
  'volumechange',
  'waiting',

  // HTMLVideoElement events
  'enterpictureinpicture',
  'leavepictureinpicture',

  // Element events
  'fullscreenchange',
  'resize',

  // video.js events
  'audioonlymodechange',
  'audiopostermodechange',
  'controlsdisabled',
  'controlsenabled',
  'debugon',
  'debugoff',
  'disablepictureinpicturechanged',
  'dispose',
  'enterFullWindow',
  'error',
  'exitFullWindow',
  'firstplay',
  'fullscreenerror',
  'languagechange',
  'loadedmetadata',
  'loadstart',
  'playerreset',
  'playerresize',
  'posterchange',
  'ready',
  'textdata',
  'useractive',
  'userinactive',
  'usingcustomcontrols',
  'usingnativecontrols',
];

function brazeAnalyticsPlugin(
  this: Player,
  options: BrazeAnalyticsPluginOptions
) {
  if (
    !options?.brazeAnalytics?.enabled ||
    !options?.brazeAnalytics?.player_logs_enabled ||
    !window?.braze?.logCustomEvent
  ) {
    return;
  }

  const eventHandlers: Record<string, (this: Player) => void> = {};

  EVENTS.forEach((event) => {
    function log(this: Player) {
      const data = {
        ...options?.trackData,
        time: this.currentTime(),
        item: options?.playableItem,
      };

      if (window?.braze?.logCustomEvent) {
        // Sends custom player event to Braze
        window.braze.logCustomEvent(`player_${event}`, data);
      }
    }

    // Throttle the event handlers to avoid flooding the analytics service
    eventHandlers[event] = throttle(log, 1000);
  });

  function onClose(this: Player) {
    removeListeners();
  }

  const removeListeners = () => {
    EVENTS.forEach((event) => this.off(event, eventHandlers[event]));
    this.off('dispose', onClose);
  };

  const addListeners = () => {
    removeListeners();
    EVENTS.forEach((event) => this.on(event, eventHandlers[event]));
    this.on('dispose', onClose);
  };

  addListeners();
}

export default brazeAnalyticsPlugin;
